import { shortDate } from '@lib/time';
import { Badge, Button, Card, CardHeader } from '@shared/ui';
import { Link, useParams } from 'react-router';

import { useClientTarget } from '../hooks/use-client-target';
import { useClient } from '../hooks/use-clients';
import { targetParams } from '../schemas/target';

import type { ClientDetail, ClientPerson } from '../schemas/client';

/**
 * What a client *is*: how it is addressed, who can reach it, what it is
 * connected to.
 *
 * Two cards, in the order an operator actually asks about them. Contact answers
 * "can anybody use it", which is the only actionable thing on this screen;
 * Integrations answers "where does its data come from".
 *
 * The identifiers are not here: they are reference rather than content, and
 * they live in the layout's rail, where they stay readable from the other tabs
 * too.
 *
 * The effective-flags view the `OrgsPage` placeholder promises is not here and
 * is not one of the tabs: it needs a per-tenant flag read this screen does not
 * have yet.
 *
 * The query is the one `ClientDetailLayout` already resolved — same target,
 * same tenant id, so the same TanStack key. See that file for why the tabs read
 * it again instead of being handed it.
 */
export function ClientGeneralTab() {
    const { tenantId = '' } = useParams();
    const { target } = useClientTarget();
    const client = useClient(target, tenantId);

    // Pending, failed and absent all belong to the layout, which does not
    // render this tab in any of them. Reaching here without data would mean
    // the tab was mounted outside that layout; nothing is the honest output.
    if (!client.data) return null;

    const data = client.data;
    const search = new URLSearchParams(targetParams(target)).toString();

    return (
        <div className="flex flex-col gap-5">
            <Card>
                <CardHeader title="Contact" subtitle={peopleSubtitle(data)} />
                <div className="p-5">
                    <PeopleSection client={data} search={search} />
                </div>
            </Card>

            <Card>
                <CardHeader title="Integrations" subtitle="Salesforce" />
                <div className="p-5">
                    <SalesforceSection client={data} />
                </div>
            </Card>
        </div>
    );
}

function peopleSubtitle(client: ClientDetail): string | undefined {
    if (!client.people) return undefined;
    return client.people.length === 1 ? '1 person' : `${String(client.people.length)} people`;
}

/**
 * Who can sign in, and how to reach them.
 *
 * Three states, and the third is not "probably fine": when the directory could
 * not be answered the honest report is that we do not know. The action stays
 * offered in that case — adding an owner to a client that already has one is
 * refused upstream, safely — but nothing is asserted either way.
 *
 * There is no login handle in this table, and that is the store's shape rather
 * than an omission: a federation handle lives in the access store as
 * `USERNAME#<handle>`, not on the directory row. Showing the email as if it
 * were what they type would be worse than not showing it.
 */
function PeopleSection({ client, search }: { client: ClientDetail; search: string }) {
    const addOwner = (
        <Link to={{ pathname: `/clients/${client.tenantId}/owner`, search }}>
            <Button>Add the owner</Button>
        </Link>
    );

    if (!client.people) {
        return (
            <div className="flex flex-col gap-4">
                <p className="text-[13px] text-content-subtle">
                    The directory could not be reached, so who can sign into this client is unknown
                    — this is not a claim that nobody can.
                </p>
                <div>{addOwner}</div>
            </div>
        );
    }

    if (client.people.length === 0) {
        return (
            <div className="flex flex-col gap-4">
                <div className="rounded-lg border border-warning-line bg-warning-surface px-3.5 py-3">
                    <p className="text-[13px] font-semibold text-warning-strong">
                        Nobody can sign into this client.
                    </p>
                    <p className="mt-1 text-[12.5px] leading-relaxed text-warning-text">
                        The directory has no people for it. Do not create the client again — its
                        address is taken by this one.
                    </p>
                </div>
                <div>{addOwner}</div>
            </div>
        );
    }

    return (
        <div className="overflow-x-auto">
            <table className="w-full border-collapse text-[13px]">
                <thead>
                    <tr>
                        <PersonTh>Contact</PersonTh>
                        <PersonTh>Status</PersonTh>
                        <PersonTh>Since</PersonTh>
                    </tr>
                </thead>
                <tbody>
                    {client.people.map((person) => (
                        <PersonRow key={person.userId} person={person} />
                    ))}
                </tbody>
            </table>
        </div>
    );
}

/**
 * What Salesforce calls this client, read-only.
 *
 * The sync button is inert on purpose and says so in the same breath. There is
 * no sync endpoint — not an unfinished one, none — and the alternative to
 * showing a disabled control was showing nothing, which loses the only place
 * the coming behaviour can be announced to the operator who would use it. A
 * live-looking button over an invented endpoint would be worse than either:
 * the failure would arrive as a network error about a route that never existed.
 *
 * The org id and subdomain are not editable here for the same reason they are
 * not editable anywhere in this app: the link is established when the client
 * registers itself through Salesforce, and re-pointing an existing tenant at a
 * different org is a data-migration question, not a form field.
 */
function SalesforceSection({ client }: { client: ClientDetail }) {
    if (!client.orgId) {
        return (
            <div className="flex flex-col gap-4">
                <p className="text-[13px] text-content-subtle">
                    Not connected. This client was created here rather than registering itself
                    through Salesforce, so there is no org to sync with.
                </p>
                <SyncAction />
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-4">
            <dl className="grid grid-cols-[190px_1fr] text-[13.5px]">
                <Row label="Salesforce Org">
                    <span className="font-mono text-[12.5px]">{client.orgId}</span>
                </Row>
                <Row label="SF subdomain">
                    {client.subdomain ?? <span className="text-content-faint">None</span>}
                </Row>
            </dl>
            <SyncAction />
        </div>
    );
}

function SyncAction() {
    return (
        <div className="flex flex-wrap items-center gap-3">
            <Button variant="ghost" disabled>
                Sync with Salesforce
            </Button>
            <span className="text-[12.5px] text-content-muted">
                Coming soon — there is no sync yet, so nothing on this card can be refreshed by
                hand.
            </span>
        </div>
    );
}

function PersonTh({ children }: { children: React.ReactNode }) {
    return (
        <th className="whitespace-nowrap border-b border-line-subtle px-0 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-content-faint">
            {children}
        </th>
    );
}

function PersonRow({ person }: { person: ClientPerson }) {
    return (
        <tr>
            <td className="border-b border-line-subtle py-3 pr-4">
                {/* A mailto, because "contact info" that cannot be acted on is
                    just text an operator has to copy by hand. */}
                <a
                    href={`mailto:${person.email}`}
                    className="font-medium text-content hover:text-accent-text hover:underline"
                >
                    {person.email}
                </a>
            </td>
            <td className="border-b border-line-subtle py-3 pr-4">
                {person.status === 'active' ? (
                    <Badge tone="success" dot>
                        {person.status}
                    </Badge>
                ) : (
                    // Anything that is not active is worth noticing: a disabled
                    // person still counts as "has people" and still cannot sign
                    // in, which is the one case a bare count would hide.
                    <Badge tone="warning" dot>
                        {person.status}
                    </Badge>
                )}
            </td>
            <td className="border-b border-line-subtle py-3 text-[12.5px] text-content-muted">
                {shortDate(person.createdAt)}
            </td>
        </tr>
    );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
    return (
        <>
            <dt className="border-b border-line-subtle py-2.5 text-[12.5px] text-content-muted">
                {label}
            </dt>
            <dd className="m-0 break-words border-b border-line-subtle py-2.5">{children}</dd>
        </>
    );
}
